/**
 * Why a credit could not be used, in words a customer can act on.
 *
 * Two different surfaces produce the same small set of facts under two
 * different vocabularies:
 *
 *  - the claim redirect lands on `?credit_error=<reason>` with the backend's
 *    `CreditTokenRejection` (`missing | invalid | expired | unknown | lapsed |
 *    spent | voided | error`);
 *  - the apply endpoint refuses with an `ApiError.code`
 *    (`CREDIT_LINK_EXPIRED`, `CREDIT_SPENT`, `ORDER_EMAIL_MISSING`, ...).
 *
 * Mapping both into ONE `CreditProblem` is the reason this file exists. The
 * alternative — a sentence list per surface — is how the coupon code came to
 * have two drifted copies of itself, and here the two surfaces genuinely say
 * the same things: an expired claim token reaches the customer as
 * `credit_error=expired` if it lapsed before they arrived and as
 * `CREDIT_LINK_EXPIRED` if it lapsed while they shopped, and they should read
 * the identical sentence either way.
 *
 * None of these is an exception. Someone clicked a link in an email about their
 * own money; the failure modes are ordinary (a tab left open for eleven
 * minutes is the commonest one by far) and every message must end with
 * something the customer can do, never a code and never a dead end.
 *
 * The backend's own `message` is deliberately NOT used. Those sentences are
 * English and describe the rule to an operator; this shop serves eight locales
 * and a customer must not be handed English prose about their money.
 */

export type CreditProblem =
  /** The exchanged claim token lapsed. Ordinary, expected, fixable by the customer. */
  | "expired"
  /** The link was mangled, truncated, or signed by someone else. */
  | "invalid"
  /** The lot is real and has nothing left on it. */
  | "spent"
  /**
   * The claim link's credit is already HELD on the claimant's own open cart
   * (`credit_error=on_your_cart`): nothing is wrong, it is waiting at
   * checkout. Never "used" (ops walk B4).
   */
  | "on-your-cart"
  /**
   * The lot was withdrawn: the order that earned it was refunded, an
   * operator voided a grant, or a gift card was cancelled. The sentence
   * names no cause, since it cannot know which. Not "spent": the customer
   * never used it, and being told they had is a support ticket (guest
   * cashback walk, B3).
   */
  | "voided"
  /** The lot itself reached its end date. */
  | "lapsed"
  /**
   * Verified, but names no credit we hold for this address. Reached WITHOUT
   * any link at all by a signed-in owner, so its sentence must not send anyone
   * back to an email.
   */
  | "unknown"
  /**
   * The credit exists (the balance says so) but an apply moved nothing onto
   * this order — typically the order is under the lot's minimum amount.
   * Saying "unknown" there told a customer with credit that they had none.
   */
  | "not-applicable"
  /** The order has no email yet, so there is nothing to match the credit against. */
  | "email-missing"
  /** The credit belongs to a different customer than this order. */
  | "email-mismatch"
  /**
   * Its price can no longer move: paid, or a card payment started and not
   * finished (price-pinned). "Already placed" is false for the second.
   */
  | "order-locked"
  /**
   * Ours, temporary, retryable — the ledger could not be read or written just
   * now. Never phrased as the customer's fault, and NEVER as missing money:
   * the credit is sitting exactly where it was.
   */
  | "busy"
  /**
   * Signed in, no claim token, and the account's email was never confirmed:
   * the server will not treat an unconfirmed registration as proof of the
   * address (anyone can register someone else's). Fixable by the customer in
   * three ways, and the sentence names all three.
   */
  | "account-unverified"
  /** Anything we have not seen. Must still be a sentence. */
  | "unavailable";

/** `?credit_error=` reasons, as CreditController redirects with them. */
const URL_REASONS: Record<string, CreditProblem> = {
  expired: "expired",
  // "no ?t= at all" is, to the person holding it, a link that does not work.
  missing: "invalid",
  invalid: "invalid",
  spent: "spent",
  on_your_cart: "on-your-cart",
  voided: "voided",
  lapsed: "lapsed",
  unknown: "unknown",
  // The ledger could not be read. Not their fault and worth retrying.
  error: "busy",
};

/** `ApiError.code` values the credit endpoints refuse with. */
const API_CODES: Record<string, CreditProblem> = {
  CREDIT_LINK_EXPIRED: "expired",
  // "you must present a token" reaches a guest only when the one they had has
  // lapsed — they had one on arrival or they would never have seen the button.
  CREDIT_PROOF_REQUIRED: "expired",
  CREDIT_LINK_INVALID: "invalid",
  CREDIT_SPENT: "spent",
  CREDIT_LAPSED: "lapsed",
  // The 503 ServiceUnavailableException proveCreditOwner throws when
  // resolveCreditToken answers `error`: the ledger was momentarily
  // unreachable. This said "unknown", which rendered as "we cannot find any
  // credit for this address" — a customer being told their money is gone
  // while it sits untouched. It is the same fact the claim redirect sends as
  // `credit_error=error`, and that one already reads as "busy".
  CREDIT_UNAVAILABLE: "busy",
  CREDIT_EMAIL_MISMATCH: "email-mismatch",
  ORDER_EMAIL_MISSING: "email-missing",
  ORDER_PRICE_PINNED: "order-locked",
  CREDIT_RECONCILE_TIMEOUT: "busy",
  CREDIT_ACCOUNT_UNVERIFIED: "account-unverified",
};

/**
 * @returns null when there was no reason at all (an ordinary page load), and
 *          "unavailable" for a reason this build does not know — a new backend
 *          reason must produce a sentence, not a blank.
 */
export function creditProblemFromUrlReason(
  reason: string | null | undefined,
): CreditProblem | null {
  if (!reason) return null;
  return URL_REASONS[reason] ?? "unavailable";
}

export function creditProblemFromApiError(error: unknown): CreditProblem {
  const code = (
    error as { response?: { data?: { code?: string } } } | undefined
  )?.response?.data?.code;
  return (code && API_CODES[code]) || "unavailable";
}

/**
 * Does this refusal condemn the claim TOKEN itself, so it will be refused
 * identically on every retry and should be dropped from storage?
 *
 * The stored token always wins over the signed-in account (it is what gets
 * sent), so a dead one kept in storage for its two hours blocks the account's
 * own credit. Only a verdict about the TOKEN itself counts: unreadable or
 * forged (`invalid`), past its two hours (`expired`), or speaking for another
 * address than this order's (`email-mismatch`), so it can never spend here.
 *
 * `spent`, `voided` and `lapsed` are verdicts about the MONEY, and deliberately not
 * here. A typed gift card applied in full leaves its lot at zero, and the
 * token is still the customer's proof of that card: dropping it sent the next
 * card's redeem without it, the answer named the new card alone, and the
 * apply after it released the first card (bug hunt A, I2). It also hid the
 * box, so an untick could not be ticked back. A signed-in, confirmed owner
 * loses nothing by the token staying: the server then serves their whole
 * address (`proveCreditOwner`). The backend now answers such a token with a
 * balance of 0 instead of CREDIT_SPENT; this rule stays as the defence for
 * any path that still refuses.
 *
 * `busy` and `unavailable` are NOT here: those say nothing about the token.
 */
export function creditTokenRefused(problem: CreditProblem): boolean {
  return (
    problem === "expired" ||
    problem === "invalid" ||
    problem === "email-mismatch"
  );
}

/**
 * The translation key and the Slovenian text that renders if a locale file is
 * missing the key — the house call form, `t(key, slovenianFallback)`.
 */
export function creditProblemMessage(problem: CreditProblem): {
  key: string;
  fallback: string;
} {
  switch (problem) {
    case "expired":
      return {
        key: "credit.errorExpired",
        fallback:
          "Ta povezava je potekla. Znova odprite povezavo v e-pošti in dobroimetje bo spet na voljo.",
      };
    case "invalid":
      return {
        key: "credit.errorInvalid",
        fallback:
          "Te povezave ni bilo mogoče prebrati. Odprite jo neposredno iz e-pošte ali nam pišite in uredili bomo.",
      };
    case "spent":
      return {
        key: "credit.errorSpent",
        // "Spent" on a claim also covers a lot whose money is HELD on an
        // open cart (the claim sees nothing left to take), so the sentence
        // names both and never tells someone with untouched credit that it
        // was used (ops walk B4).
        fallback:
          "To dobroimetje je že porabljeno ali pa je dodano v eno od vaših odprtih košaric, zato ga ni mogoče znova uporabiti.",
      };
    case "on-your-cart":
      return {
        key: "credit.errorSpentOnThisCart",
        fallback:
          "To dobroimetje je že dodano v vašo trenutno košarico. Upoštevali ga bomo pri zaključku naročila.",
      };
    case "voided":
      return {
        key: "credit.errorVoided",
        fallback:
          "To dobroimetje ni več na voljo, ker je bilo preklicano.",
      };
    case "lapsed":
      return {
        key: "credit.errorLapsed",
        fallback:
          "Veljavnost tega dobroimetja se je iztekla. Če menite, da je prišlo do napake, nam pišite.",
      };
    case "unknown":
      // Says nothing about a link. A signed-in owner needs no token, so this
      // is routinely read by someone who never clicked one — and "open the
      // link in your email again" is then a dead end dressed as an
      // instruction. Writing to us is the one route that always exists.
      return {
        key: "credit.errorUnknown",
        fallback:
          "Za ta e-naslov ne najdemo dobroimetja, ki bi ga lahko uporabili pri tem naročilu. Če menite, da je prišlo do napake, nam pišite.",
      };
    case "not-applicable":
      return {
        key: "credit.errorNotApplicable",
        fallback:
          "Dobroimetja pri tem naročilu ni mogoče uporabiti. Preverite, ali naročilo dosega najnižji znesek za vaše dobroimetje.",
      };
    case "email-missing":
      return {
        key: "credit.errorEmailMissing",
        fallback:
          "Najprej vpišite e-naslov za to naročilo, nato bomo lahko dobroimetje uporabili.",
      };
    case "email-mismatch":
      return {
        key: "credit.errorEmailMismatch",
        fallback:
          "To dobroimetje pripada drugemu e-naslovu. Vpišite e-naslov, na katerega smo poslali obvestilo o dobroimetju.",
      };
    case "order-locked":
      return {
        key: "credit.errorOrderLocked",
        fallback:
          "Plačilo za to naročilo je že v teku ali zaključeno, zato dobroimetja ni več mogoče spreminjati.",
      };
    case "busy":
      return {
        key: "credit.errorBusy",
        fallback:
          "Dobroimetja trenutno ni bilo mogoče urediti. Poskusite znova čez nekaj trenutkov.",
      };
    case "account-unverified":
      return {
        key: "credit.errorAccountUnverified",
        fallback:
          "Vaš e-naslov še ni potrjen, zato dobroimetja na računu ne moremo uporabiti. Potrdite ga prek povezave, ki smo vam jo poslali ob registraciji, odprite povezavo v e-sporočilu o dobroimetju ali ponastavite geslo.",
      };
    case "unavailable":
    default:
      return {
        key: "credit.errorUnavailable",
        fallback:
          "Dobroimetja tokrat ni bilo mogoče uporabiti. Poskusite znova ali nam pišite in uredili bomo.",
      };
  }
}

/**
 * The sentence for a refused CLAIM link (`?credit_error=`).
 *
 * The claim answers `spent` both for credit that was used and for credit
 * whose money sits on an open cart — the customer's own, typically, after
 * clicking the email link a second time (ops walk B4). The reason code cannot
 * tell the two apart, but this browser can: when the cart it is working on
 * already carries credit, the customer is told it is on that cart, not that
 * it is used. Every other case is `creditProblemMessage`.
 */
export function claimProblemMessage(
  problem: CreditProblem,
  ctx: { cartHoldsCredit: boolean },
): { key: string; fallback: string } {
  if (problem === "spent" && ctx.cartHoldsCredit) {
    return creditProblemMessage("on-your-cart");
  }
  return creditProblemMessage(problem);
}
